<template>
  <page-title title="Discussions" />
  <div class="container mx-auto grow space-y-16">
    <div class="w-full">
      <div class="mb-4 flex flex-row text-sm flex-wrap space-x-2">
        <div class="top-filter">
          <span>Status:</span>

          <inline-select
              class="select"
              v-model="status"
              :items="statusOptions">
            <template #default="{option}">
              <option :value="option.value">
                {{ option.label }}
              </option>
            </template>
          </inline-select>
        </div>

        <div class="top-filter">
          <span>Medium:</span>
          <inline-select
              class="select"
              v-model="medium"
              :items="mediumOptions">
            <template #default="{option}">
              <option :value="option.value">
                {{ option.label }}
              </option>
            </template>
          </inline-select>
        </div>

        <div
            v-if="!automationsLoading"
            class="top-filter">
          <span>Automation:</span>
          <inline-select
              class="select "
              v-model="selectedAutomation"
              :items="automationsToShow">
            <template #default="{option}">
              <option :value="option.id">
                {{ option.name }}
              </option>
            </template>
            <template #empty>
              <option :value="null">All</option>
            </template>
          </inline-select>
        </div>

        <div
            v-if="currentTeam && currentTeam.locations.length > 1"
            class="top-filter ">
          <span>Location:</span>
          <inline-select
              class="select"
              v-model="selectedLocation"
              :items="currentTeam.locations">
            <template #default="{option}">
              <option :value="option.id">
                {{ option.name }}
              </option>
            </template>
            <template #empty>
              <option :value="null">All</option>
            </template>
          </inline-select>
        </div>

        <div class="top-filter">
          <span>Last Contacted:</span>
          <inline-datepicker v-model:dates="dateRange" placeholder="Select range..." />
        </div>

        <div class="text-gray-700 md:text-right grow flex flex-col items-end">
          <div class="top-filter text-right">
            <span>Per page:</span>
            <inline-select
                class="select text-right"
                v-model="paginationData.perPage"
                :items="perPageOptions">
              <template #default="{option}">
                <option :value="option.value">
                  {{ option.label }}
                </option>
              </template>
            </inline-select>
          </div>
        </div>
      </div>

      <empty v-if="loadedFirstTime && discussions.length === 0">
        <template #title>
          Nothing to show!
        </template>
        <template #subtitle>
          There aren't any discussions with the current filters
        </template>
      </empty>

      <div
          v-if="!loadedFirstTime"
          class="w-full h-40 px-2 my-2">
        <div class="flex h-full items-center justify-center">
          <div class="w-12 h-12 text-gray-400 opacity-75">
            <loader />
          </div>
        </div>
      </div>

      <div
          v-if="loadedFirstTime && discussions.length > 0"
          class="flex flex-col">
        <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
            <div class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
              <table class=" divide-y divide-gray-200 min-w-full">
                <thead class="bg-gray-50">
                <tr>
                  <th scope="col" class="pl-4 py-4 w-12">
                    <div class="flex items-center justify-center">
                      <input
                          type="checkbox"
                          class="form-checkbox h-4 w-4 text-indigo-600 transition duration-150 ease-in-out"
                          :checked="isAllSelected"
                          @change="toggleSelectAll"
                          :disabled="selectableDiscussions.length === 0"
                      />
                      <span class="sr-only">Select All</span>
                    </div>
                  </th>
                  <th
                      scope="col"
                      class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client
                  </th>
                  <th
                      scope="col"
                      class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th
                      scope="col"
                      class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-full">
                    Information
                  </th>
                  <th
                      scope="col"
                      class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th
                      scope="col"
                      class="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-right">
                    Date
                  </th>
                </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                <single-discussion
                    v-for="discussion in discussions"
                    :key="discussion.id"
                    :discussion="discussion"
                    :selected-ids="selectedDiscussions"
                    :failed-close-ids="failedCloseIds"
                    @selection-change="handleSelectionChange" />
                </tbody>
                <tfoot
                    class="bg-gray-50 divide-y divide-gray-200">
                <tr v-if="selectedDiscussions.length > 0">
                  <td
                      colspan="6"
                      class="p-4">
                    <div class="flex justify-start">
                      <v-button
                          class="btn btn-red"
                          :loading="isClosing"
                          :disabled="isClosing"
                          @click="bulkCloseDiscussions">
                        Close {{ selectedDiscussions.length }} Selected
                      </v-button>
                    </div>
                  </td>
                </tr>
                <tr v-if="discussions && paginatorInfo.total > 0&& paginatorInfo.lastPage > 1">
                  <td
                      colspan="6"
                      class="p-4">
                    <pagination
                        v-if="paginatorInfo.lastPage > 1"
                        :pagination="paginatorInfo"
                        class="flex items-center justify-between text-gray-700 text-sm"
                        @set-page="paginationData.page = $event">
                      <div
                          v-if="loading"
                          class="inline-block w-8 h-8 mr-2 text-gray-400">
                        <loader />
                      </div>
                    </pagination>
                  </td>
                </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>
<style scoped>
.top-filter {
  @apply text-gray-700 flex items-center space-x-2;
}

.top-filter span {
  @apply whitespace-nowrap;
}

.top-filter .select {
  @apply text-sm font-bold w-full truncate grow;
  max-width:10em;
}

</style>
<script lang="ts" setup>
import PageTitle                                             from "@/components/navbar/PageTitle.vue";
import Empty                                                 from "@/components/layout/Empty.vue";
import SingleDiscussion                                      from "@/components/discussions/listing/SingleDiscussion.vue";
import Pagination                                            from "@/components/layout/Pagination.vue";
import Loader                                                from "@/components/layout/Loader.vue";
import { computed, ref, watch }                              from "vue";
import { QLAutomation, QLAutomationDeliveryTypeEnum, QLDiscussionsQuery, QLDiscussionsQueryVariables, QLSortOrder, useAutomationsQuickListQuery, useDiscussionsQuery, useDiscussionUpdateMutation } from "@/graphql/queries/ql/composables";
import VButton                                               from "@/components/layout/VButton.vue";
import currentTeamQuery                                      from "@/composables/queries/currentTeamQuery";
import { createPaginationData, paginationWithAlreadyLoaded } from "@/composables/paginationWithAlreadyLoaded";
import InlineSelect                                          from "@/components/layout/Forms/InlineSelect.vue";
import InlineDatepicker                                      from "@/components/layout/Forms/InlineDatepicker.vue";
import { echoListener }                                      from "@/composables/echoListener";
import { EventTypes }                                        from "@/classes/notifications/EventTypes";

enum StatusOptions {
  "Open", "Closed", "Responded", "All"
}

enum MediumOptions {
  "Any", "Sms", "Email"
}

const status = ref<StatusOptions>(StatusOptions.Open);
const medium = ref<MediumOptions>(MediumOptions.Any);

// Multi-select state
const selectedDiscussions = ref<string[]>([]);
const failedCloseIds = ref<string[]>([]);
const isClosing = ref(false);

// Date range filter
const dateRange = ref([]);

const statusOptions = [
  { label: "Open", value: StatusOptions.Open },
  { label: "Closed", value: StatusOptions.Closed },
  { label: "All", value: StatusOptions.All }
];
const mediumOptions = [
  { label: "Any", value: MediumOptions.Any },
  { label: "Sms", value: MediumOptions.Sms },
  { label: "Email", value: MediumOptions.Email }
];

const perPageOptions = [
  { label: 10, value: 10 },
  { label: 25, value: 25 },
  { label: 50, value: 50 }
];

const { paginationData } = createPaginationData({
                                                 page:     1,
                                                 perPage:  10,
                                                 perPages: [ 10, 25, 50 ]
                                               });

const selectedAutomation = ref(null);
const selectedLocation = ref(null);

function resetPage() {
  return () => {
    paginationData.page = 1;
    selectedDiscussions.value = []; // Clear selections when filters change
  };
}

// Handle checkbox selection changes
function handleSelectionChange({ id, checked }: { id: string, checked: boolean }) {
  if (checked) {
    if (!selectedDiscussions.value.includes(id)) {
      selectedDiscussions.value.push(id);
    }
  } else {
    selectedDiscussions.value = selectedDiscussions.value.filter(selId => selId !== id);
  }
}

// Get only selectable discussions (not closed)
const selectableDiscussions = computed(() => {
  return discussions.value.filter(discussion => !discussion.closed);
});

// Check if all selectable discussions are selected
const isAllSelected = computed(() => {
  const selectableCount = selectableDiscussions.value.length;
  if (selectableCount === 0) {
    return false;
  }

  // Get IDs of selectable discussions
  const selectableIds = selectableDiscussions.value.map(discussion => discussion.id);

  // Check if all selectable discussions are in the selectedDiscussions array
  return selectableIds.every(id => selectedDiscussions.value.includes(id));
});

// Toggle select all discussions
function toggleSelectAll(event: Event) {
  const isChecked = (event.target as HTMLInputElement).checked;
  
  if (isChecked) {
    // Select all selectable discussions
    selectedDiscussions.value = selectableDiscussions.value.map(discussion => discussion.id);
  } else {
    // Deselect all discussions
    selectedDiscussions.value = [];
  }
}

// Bulk close discussions
const discussionUpdateMutation = useDiscussionUpdateMutation({});

async function bulkCloseDiscussions() {
  if (selectedDiscussions.value.length === 0) return;

  isClosing.value = true;
  failedCloseIds.value = []; // Clear previous failures
  const discussionsToClose = [...selectedDiscussions.value]; // Copy selection before clearing
  
  const currentFailures: string[] = [];
  
  // Process each discussion one by one
  for (const id of discussionsToClose) {
    try {
      await discussionUpdateMutation.mutate({ id, closed: true });
    } catch (error) {
      // If there's an error, add the ID to the failures list
      currentFailures.push(id);
      console.error(`Failed to close discussion ${id}:`, error);
    }
  }

  failedCloseIds.value = currentFailures;
  isClosing.value = false;
  selectedDiscussions.value = []; // Clear selection after processing
  await discussionsQ.refetch(); // Refetch the list to show updated statuses

  if (failedCloseIds.value.length > 0) {
    // Set timeout to clear visual feedback after 5 seconds
    setTimeout(() => {
      failedCloseIds.value = [];
    }, 5000);
  }
}

watch(() => status, resetPage());
watch(() => medium, resetPage());
watch(() => selectedAutomation, resetPage());
watch(() => selectedLocation, resetPage());
watch(() => dateRange, resetPage());

const discusssionsVariables = computed<QLDiscussionsQueryVariables>(() => {
  let q: QLDiscussionsQueryVariables = {
    page:    paginationData.page,
    first:   paginationData.perPage,
    orderBy: [ { column: "last_contacted_at", order: QLSortOrder.DESC } ]
  };

  if (status.value === StatusOptions.Open) {
    q.closed = false;
  }
  if (status.value === StatusOptions.Closed) {
    q.closed = true;
  }
  if (status.value === StatusOptions.Responded) {
    q.team_has_responded = true;
  }
  if (medium.value === MediumOptions.Sms) {
    q.sms = true;
  }
  if (medium.value === MediumOptions.Email) {
    q.email = true;
  }
  if (selectedAutomation.value) {
    q.automation_id = selectedAutomation.value;
  }

  if (selectedLocation.value) {
    q.home_location_id = selectedLocation.value;
  }

  // Add date range filter parameters if dates are selected
  if (dateRange.value && dateRange.value.length === 2) {
    // Convert dates to Unix timestamps for the backend
    const formatStartDate = (date: string) => {
      // Create a date object and get the timestamp in seconds (beginning of day)
      return Math.floor(new Date(date).getTime() / 1000);
    };
    
    const formatEndDate = (date: string) => {
      // Create a date object for the end of the selected day
      const endDate = new Date(date);
      endDate.setHours(23, 59, 59, 999); // Set to 23:59:59.999
      return Math.floor(endDate.getTime() / 1000);
    };
    
    q.last_contacted_at_from = formatStartDate(dateRange.value[0]);
    q.last_contacted_at_to = formatEndDate(dateRange.value[1]);
  }

  
  return q;
});

const discussionsQ = useDiscussionsQuery(discusssionsVariables);

const {
  paginatorInfo,
  loadedFirstTime,
  results: discussions,
  loading
} = paginationWithAlreadyLoaded<QLDiscussionsQuery["discussions"]["data"]>(discussionsQ);

echoListener([ EventTypes.DiscussionNew, EventTypes.DiscussionUpdated ], (ev) => {
  discussionsQ.refetch();
}, 5000);

const { result: automationsResults, loading: automationsLoading } = useAutomationsQuickListQuery();
const automationsRes = computed(() => automationsResults.value?.automations ?? []);
const automationsToShow = computed(() => {
  return automationsRes.value.filter((aut: QLAutomation) => aut.delivery_type == QLAutomationDeliveryTypeEnum.EMAIL || aut.delivery_type == QLAutomationDeliveryTypeEnum.SMS);
});

const { currentTeam } = currentTeamQuery();

</script>
